import { WebpEgwBlackLogo } from '@egw/assets'
import { type TFunction } from '@egw/i18n'
import { jsPDF } from 'jspdf'
import { autoTable } from 'jspdf-autotable'

type jsPDFType = any

interface GeneratePDFDownloadProps {
  bankName?: string
  routingNumber?: string
  accountNumber?: string
  addressLine1?: string
  addressLine2?: string
  addressCity?: string
  addressRegionCode?: string
  addressPostalCode?: string
  firstName?: string
  lastName?: string
  productName?: string
  memo?: string
  t: TFunction<'features-enrollment'>
}

export const GeneratePDFDownload = ({
  bankName,
  routingNumber,
  accountNumber,
  addressLine1,
  addressLine2,
  addressCity,
  addressRegionCode,
  addressPostalCode,
  firstName,
  lastName,
  productName,
  memo,
  t,
}: GeneratePDFDownloadProps): void => {
  // eslint-disable-next-line new-cap
  const doc = new jsPDF('p', 'mm', 'a4') as unknown as jsPDFType

  const image = new Image()
  image.src = WebpEgwBlackLogo

  doc.addImage(image, 'PNG', 10, 10, 120, 8)

  const h1 = t('wire_instructions_pdf.h1', { productName })
  const p1 = t('wire_instructions_pdf.p1', { productName })
  const p2 = t('wire_instructions_pdf.p2')
  const p3 = t('wire_instructions_pdf.p3')
  const p4 = t('wire_instructions_pdf.p4')
  const p4_5 = t('wire_instructions_pdf.p4_5')
  const h2 = t('wire_instructions_pdf.wire_instructions')

  doc.setFontSize(15)
  doc.setFont('Helvetica', 'bold')

  doc.text(h1, 10, 30)

  doc.setFontSize(11)
  doc.setFont('Helvetica', 'normal')
  const splitp1 = doc.splitTextToSize(p1, 180)
  doc.text(10, 40, splitp1)

  const splitp2 = doc.splitTextToSize(p2, 180)
  doc.text(10, 65, splitp2)

  const splitp3 = doc.splitTextToSize(p3, 180)
  doc.text(10, 75, splitp3)

  const splitp4 = doc.splitTextToSize(p4, 180)
  doc.text(10, 95, splitp4)

  doc.setFont('Helvetica', 'bold')
  const splitp4_5 = doc.splitTextToSize(p4_5, 180)
  doc.text(10, 100, splitp4_5)

  doc.setFontSize(15)
  const splith2 = doc.splitTextToSize(h2, 180)
  doc.text(10, 115, splith2)

  const beneficiaryAddressLabel = `${addressLine1}\n${addressLine2 ? `${addressLine2}\n${addressCity}` : `${addressCity}`} ${addressRegionCode}, ${addressPostalCode}`

  // We do the account data table here
  const tableRows = [
    [t('wire_instructions_pdf.bank'), bankName ?? ''],
    [t('wire_instructions_pdf.routing_number_label'), routingNumber ?? ''],
    [t('wire_instructions_pdf.account_number_label'), accountNumber ?? ''],
    [t('wire_instructions_pdf.beneficiary_label'), `${firstName} ${lastName}`],
    [t('wire_instructions_pdf.beneficiary_address_label'), beneficiaryAddressLabel],
    [t('wire_instructions_pdf.bank_address_label'), t('wire_instructions_pdf.bank_address')],
    [t('wire_instructions_pdf.memo'), memo ?? ''],
  ]

  autoTable(doc, {
    body: tableRows,
    startY: 120,
    bodyStyles: {
      fillColor: [255, 255, 255],
      fontSize: 12, // Adjust the font size for the body
      cellPadding: { top: 5, right: 5, bottom: 5, left: 5 }, // Adjust cell padding
      textColor: [0, 0, 0], // Set text color for the body
    },
  })

  // Save the PDF
  doc.save('wire-transfer-instructions.pdf')
}

interface GenerateInvestmentWirePDFDownloadProps {
  t: TFunction<'features-transfer'>
  accountNumber?: string
  accountTitle?: string
}

export const GenerateInvestmentWirePDFDownload = ({
  t,
  accountNumber,
  accountTitle,
}: GenerateInvestmentWirePDFDownloadProps) => {
  // eslint-disable-next-line new-cap
  const doc = new jsPDF('p', 'mm', 'a4') as unknown as jsPDFType

  const image = new Image()
  image.src = WebpEgwBlackLogo

  doc.addImage(image, 'PNG', 10, 10, 120, 8)

  const h1 = t('tabbed_wire_transfer.receive_transfer.apex_h1')
  const p1 = t('tabbed_wire_transfer.receive_transfer.apex_p1')
  const p2 = t('tabbed_wire_transfer.receive_transfer.apex_p2')
  const p3 = t('tabbed_wire_transfer.receive_transfer.apex_p3')
  const h2 = t('tabbed_wire_transfer.receive_transfer.apex_wire_instructions')

  doc.setFontSize(15)
  doc.setFont('Helvetica', 'bold')

  doc.text(h1, 10, 30)

  doc.setFontSize(11)
  doc.setFont('Helvetica', 'normal')
  const splitp1 = doc.splitTextToSize(p1, 180)
  doc.text(10, 40, splitp1)

  const splitp2 = doc.splitTextToSize(p2, 180)
  doc.text(10, 60, splitp2)

  const splitp3 = doc.splitTextToSize(p3, 180)
  doc.text(10, 80, splitp3)

  doc.setFontSize(15)
  const splith2 = doc.splitTextToSize(h2, 180)
  doc.text(10, 115, splith2)

  const beneficiaryAddressLine1 = t('tabbed_wire_transfer.receive_transfer.apex_beneficiary_address_line1')
  const beneficiaryAddressLine2 = t('tabbed_wire_transfer.receive_transfer.apex_beneficiary_address_line2')
  const beneficiaryAddressCity = t('tabbed_wire_transfer.receive_transfer.apex_beneficiary_address_city')
  const beneficiaryAddressRegionCode = t('tabbed_wire_transfer.receive_transfer.apex_beneficiary_address_region_code')
  const beneficiaryAddressPostalCode = t('tabbed_wire_transfer.receive_transfer.apex_beneficiary_address_postal_code')

  const beneficiaryAddressLabel = `${beneficiaryAddressLine1}\n${beneficiaryAddressLine2 ? `${beneficiaryAddressLine2}\n${beneficiaryAddressCity}` : beneficiaryAddressCity} ${beneficiaryAddressRegionCode} ${beneficiaryAddressPostalCode}`

  // We do the account data table here
  const tableRows = [
    [t('tabbed_wire_transfer.receive_transfer.bank_label'), t('tabbed_wire_transfer.receive_transfer.bmo_harris_bank')],
    [
      t('tabbed_wire_transfer.receive_transfer.routing_number_label'),
      t('tabbed_wire_transfer.receive_transfer.apex_routing_number'),
    ],
    [
      t('tabbed_wire_transfer.receive_transfer.account_number_label'),
      t('tabbed_wire_transfer.receive_transfer.apex_account_number'),
    ],
    [
      t('tabbed_wire_transfer.receive_transfer.beneficiary_label'),
      t('tabbed_wire_transfer.receive_transfer.apex_beneficiary'),
    ],
    [t('tabbed_wire_transfer.receive_transfer.beneficiary_address_label'), beneficiaryAddressLabel],
    [
      t('tabbed_wire_transfer.receive_transfer.bank_address_label'),
      t('tabbed_wire_transfer.receive_transfer.apex_bank_address'),
    ],
    [
      t('tabbed_wire_transfer.receive_transfer.memo'),
      t('tabbed_wire_transfer.receive_transfer.apex_enter_as_needed', { accountTitle, accountNumber }),
    ],
  ]

  autoTable(doc, {
    body: tableRows,
    startY: 120,
    bodyStyles: {
      fillColor: [255, 255, 255],
      fontSize: 12, // Adjust the font size for the body
      cellPadding: { top: 5, right: 5, bottom: 5, left: 5 }, // Adjust cell padding
      textColor: [0, 0, 0], // Set text color for the body
    },
  })

  // Save the PDF
  doc.save('wire-transfer-instructions.pdf')
}
